import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react'
import * as React from 'react'
import {
  type DayButton,
  DayPicker,
  getDefaultClassNames,
} from 'react-day-picker'
import { twMerge } from 'tailwind-merge'

import { Button } from './button'

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={twMerge(
        'bg-background group/calendar p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        String.raw`[.rdp-button\_next>svg]:**:rtl:rotate-180`,
        String.raw`[.rdp-button\_previous>svg]:**:rtl:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: twMerge('w-fit', defaultClassNames.root),
        months: twMerge(
          'flex gap-4 flex-col md:flex-row relative ',
          defaultClassNames.months,
        ),
        month: twMerge('flex flex-col w-full gap-4', defaultClassNames.month),
        nav: twMerge(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav,
        ),
        button_previous: twMerge(
          'size-(--cell-size) text-monochromatic-500 aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_previous,
        ),
        button_next: twMerge(
          'size-(--cell-size) text-monochromatic-500 aria-disabled:opacity-50 p-0 select-none',
          defaultClassNames.button_next,
        ),
        month_caption: twMerge(
          'flex items-center text-monochromatic-500 justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: twMerge(
          'w-full flex items-center text-monochromatic-500 text-sm font-medium justify-center h-(--cell-size) gap-1.5',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: twMerge(
          'relative cn-calendar-dropdown-root rounded-(--cell-radius)',
          defaultClassNames.dropdown_root,
        ),
        dropdown: twMerge(
          'absolute bg-popover inset-0 opacity-0',
          defaultClassNames.dropdown,
        ),
        caption_label: twMerge(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : 'cn-calendar-caption-label rounded-(--cell-radius) flex items-center gap-1 text-sm  [&>svg]:text-monochromatic-500 [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: twMerge('flex', defaultClassNames.weekdays),
        weekday: twMerge(
          'text-monochromatic-500 rounded-(--cell-radius) flex-1 font-normal text-[0.8rem] select-none',
          defaultClassNames.weekday,
        ),
        week: twMerge('flex w-full mt-2', defaultClassNames.week),
        week_number_header: twMerge(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header,
        ),
        week_number: twMerge(
          'text-[0.8rem] select-none text-monochromatic-500',
          defaultClassNames.week_number,
        ),
        day: twMerge(
          'relative w-full rounded-(--cell-radius) h-full p-0 text-center [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius) group/day aspect-square select-none',
          props.showWeekNumber
            ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)'
            : '[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)',
          defaultClassNames.day,
        ),
        range_start: twMerge(
          'rounded-l-(--cell-radius) bg-transparent relative after:bg-transparent after:absolute after:inset-y-0 after:w-4 after:right-0 z-0 isolate',
          defaultClassNames.range_start,
        ),
        range_middle: twMerge('rounded-none', defaultClassNames.range_middle),
        range_end: twMerge(
          'rounded-r-(--cell-radius) relative after:bg-transparent after:absolute after:inset-y-0 after:w-4 after:left-0 z-0 isolate',
          defaultClassNames.range_end,
        ),
        today: twMerge(
          'rounded-(--cell-radius) data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: twMerge(
          'text-monochromatic-500 aria-selected:text-monochromatic-500',
          defaultClassNames.outside,
        ),
        disabled: twMerge(
          'text-monochromatic-500 opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: twMerge('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={twMerge(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon
                className={twMerge('size-4', className)}
                {...props}
              />
            )
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={twMerge('size-4', className)}
                {...props}
              />
            )
          }

          return (
            <ChevronDownIcon
              className={twMerge('size-4', className)}
              {...props}
            />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="size-(--cell-size) flex items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={twMerge(
        'data-[selected-single=true]:text-primary-foreground min-w-(--cell-size) relative isolate z-10 flex aspect-square size-8 w-full flex-col gap-1 border-0 font-normal leading-none data-[selected-single=true]:bg-bios-primary',
        'group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-bios-50',
        '[&>span]:text-xs [&>span]:opacity-70',
        'data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-bios-primary data-[range-start=true]:text-white',
        'data-[range-middle=true]:rounded-(--cell-radius) data-[range-middle=true]:bg-bios-primary/20 data-[range-middle=true]:text-bios-primary',
        'data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-bios-primary data-[range-end=true]:text-white',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
